/**
 * @graphql cc
 */
import gql from 'graphql-tag';
import {
	// eslint-disable-next-line graphql-relay-compat/no-import-apollo-functions -- Read http://go/cfe-no-import-apollo-functions
	useMutation,
	// eslint-disable-next-line graphql-relay-compat/no-import-apollo-functions -- Read http://go/cfe-no-import-apollo-functions
	useQuery,
} from '@apollo/react-hooks';
import type { DataProxy } from 'apollo-cache';

import { transformToNote } from '../dataTransformers';
import { GetNotesQuery } from '../NotesQuery.graphql';

import type {
	useNotesDataDeleteNoteMutation as useNotesDataDeleteNoteMutation$data,
	useNotesDataDeleteNoteMutationVariables as useNotesDataDeleteNoteMutation$variables,
} from './__types__/useNotesDataDeleteNoteMutation';
import type {
	useNotesDataUpdateNoteMutation as useNotesDataUpdateNoteMutation$data,
	useNotesDataUpdateNoteMutationVariables as useNotesDataUpdateNoteMutation$variables,
} from './__types__/useNotesDataUpdateNoteMutation';
import type {
	useNotesDataCreateNoteMutation as useNotesDataCreateNoteMutation$data,
	useNotesDataCreateNoteMutationVariables as useNotesDataCreateNoteMutation$variables,
} from './__types__/useNotesDataCreateNoteMutation';
import type {
	useNotesDataNotesByCreatorQuery as useNotesDataNotesByCreatorQuery$data,
	useNotesDataNotesByCreatorQueryVariables as useNotesDataNotesByCreatorQuery$variables,
	useNotesDataNotesByCreatorQuery_notesByCreator_NoteConnection_nodes_NoteResponse,
} from './__types__/useNotesDataNotesByCreatorQuery';

const EAP_MAX_LIMIT_OF_NOTES = 50;

const getNotesQueryOptions = {
	query: GetNotesQuery,
	variables: { limit: EAP_MAX_LIMIT_OF_NOTES },
};

// Todo: add pagination logic, error handling, and analytics
export const useNotesData = () => {
	const { error, loading, data } = useQuery<
		useNotesDataNotesByCreatorQuery$data,
		useNotesDataNotesByCreatorQuery$variables
	>(
		gql`
			query useNotesDataNotesByCreatorQuery($limit: Int!) {
				notesByCreator(input: { isAscending: true, first: $limit }) {
					nodes {
						ari
						title
						body
						bodyExcerpt
						createdAt
						updatedAt
						isPinned
						backgroundColor
					}
				}
			}
		`,
		{
			...getNotesQueryOptions,
			errorPolicy: 'all',
		},
	);

	const transformDataToCacheFormat = (
		currentCache: useNotesDataNotesByCreatorQuery$data,
		dataToCache: Array<useNotesDataNotesByCreatorQuery_notesByCreator_NoteConnection_nodes_NoteResponse | null>,
	) => {
		return {
			...currentCache,
			notesByCreator: {
				...currentCache.notesByCreator,
				nodes: dataToCache,
			},
		};
	};

	const readCache = (cache: DataProxy) =>
		cache.readQuery<useNotesDataNotesByCreatorQuery$data>(getNotesQueryOptions);

	const writeCache = (
		cache: DataProxy,
		notesByCreatorData: useNotesDataNotesByCreatorQuery$data,
	) => {
		cache.writeQuery({
			...getNotesQueryOptions,
			data: notesByCreatorData,
		});
	};

	const [createNoteMutation, { loading: createNoteLoading }] = useMutation<
		useNotesDataCreateNoteMutation$data,
		useNotesDataCreateNoteMutation$variables
	>(
		gql`
			mutation useNotesDataCreateNoteMutation($title: String, $body: String) {
				createNote(input: { title: $title, body: $body }) {
					errors {
						message
					}
					success
					note {
						ari
						title
						body
						bodyExcerpt
						collectionName
						isPinned
						thumbnailId
						labels
						createdAt
						updatedAt
						backgroundColor
						extraProps {
							key
							value
						}
					}
				}
			}
		`,
		{
			update: (cache, { data: responseData }) => {
				const cachedData = readCache(cache);

				if (cachedData?.notesByCreator?.nodes && responseData?.createNote?.note) {
					const newCacheData = [responseData.createNote.note, ...cachedData.notesByCreator.nodes];
					writeCache(cache, transformDataToCacheFormat(cachedData, newCacheData));
				}
			},
		},
	);

	const [updateNoteMutation] = useMutation<
		useNotesDataUpdateNoteMutation$data,
		useNotesDataUpdateNoteMutation$variables
	>(
		gql`
			mutation useNotesDataUpdateNoteMutation(
				$ari: String!
				$title: String
				$body: String
				$isPinned: Boolean = false
				$backgroundColor: String
			) {
				updateNote(
					input: {
						title: $title
						body: $body
						backgroundColor: $backgroundColor
						metadata: { ari: $ari, isPinned: $isPinned }
					}
				) {
					errors {
						message
						extensions {
							errorType
							statusCode
						}
					}
					success
					note {
						ari
						title
						body
						bodyExcerpt
						collectionName
						isPinned
						updatedAt
						backgroundColor
					}
				}
			}
		`,
		{
			update: (cache, { data: responseData }) => {
				const cachedData = readCache(cache);
				if (cachedData?.notesByCreator?.nodes && responseData?.updateNote?.note?.ari) {
					// find old cache note index
					const cachedNoteIndex = cachedData.notesByCreator.nodes.findIndex(
						(note) => note?.ari === responseData.updateNote?.note?.ari,
					);
					if (cachedNoteIndex >= 0) {
						// if exist get the cache note data
						const cachedNoteData = cachedData.notesByCreator.nodes[cachedNoteIndex];
						if (cachedNoteData) {
							// if data is not null, delete cache note and add updated note to top
							const updatedNoteData = {
								...cachedNoteData,
								...responseData.updateNote?.note,
							} as useNotesDataNotesByCreatorQuery_notesByCreator_NoteConnection_nodes_NoteResponse | null; // added null to keep types happy
							const newCacheData = [updatedNoteData].concat(
								cachedData.notesByCreator.nodes.slice(0, cachedNoteIndex),
								cachedData.notesByCreator.nodes.slice(cachedNoteIndex + 1),
							);
							writeCache(cache, transformDataToCacheFormat(cachedData, newCacheData));
						}
					}
				}
			},
		},
	);

	const [
		deleteNoteMutation,
		{ error: deleteNoteError, loading: deleteNoteLoading, data: deleteNoteData },
	] = useMutation<useNotesDataDeleteNoteMutation$data, useNotesDataDeleteNoteMutation$variables>(
		gql`
			mutation useNotesDataDeleteNoteMutation($ari: String!) {
				deleteNote(input: { ari: $ari }) {
					errors {
						message
					}
					success
					ari
				}
			}
		`,
		{
			update: (cache, { data: responseData }) => {
				const cachedData = readCache(cache);

				if (cachedData?.notesByCreator?.nodes && responseData?.deleteNote?.ari) {
					const newCacheData = cachedData.notesByCreator.nodes.filter(
						(note) => note?.ari !== responseData?.deleteNote?.ari,
					);

					writeCache(cache, transformDataToCacheFormat(cachedData, newCacheData));
				}
			},
		},
	);

	const all = data?.notesByCreator;
	const notes = (all?.nodes || []).map((note) => transformToNote(note || undefined));

	// This has been created just for the early access program since we are limiting users to only 50 notes.
	const userHasMaxNumberOfNotes = notes.length >= EAP_MAX_LIMIT_OF_NOTES;

	return {
		notesData: notes,
		createNote: createNoteMutation,
		createNoteLoading,
		deleteNote: deleteNoteMutation,
		deleteNoteError,
		deleteNoteLoading,
		deleteNoteData,
		updateNote: updateNoteMutation,
		loading,
		error,
		userHasMaxNumberOfNotes,
	};
};
